import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import { inputClass, labelClass, sectionClass, primaryBtnClass } from "@/lib/styleClasses"

interface BvcConfigStatus {
    hasToken: boolean
    enabled: boolean
    testedAt: string | null
    bvcDisplayName: string | null
    env: "test" | "prod"
}

export const BeskydCardSection = ({ arealId }: { arealId: string }) => {
    const { t } = useTranslation()
    const qc = useQueryClient()
    const [tokenInput, setTokenInput] = useState("")
    const [lastTestedName, setLastTestedName] = useState<string | null>(null)

    const { data: status, isLoading } = useQuery({
        queryKey: ["bvc-config", arealId],
        queryFn: () => api.get<BvcConfigStatus>(`/areals/${arealId}/beskyd-card-config`),
    })

    const saveTokenMutation = useMutation({
        mutationFn: (accessToken: string) =>
            api.patch<{ message: string }>(`/areals/${arealId}/beskyd-card-config`, { accessToken }),
        onSuccess: () => {
            toast.success(t("admin.settings.bvc.saved"))
            setTokenInput("")
            void qc.invalidateQueries({ queryKey: ["bvc-config", arealId] })
        },
        onError: () => toast.error(t("admin.settings.bvc.saveFailed")),
    })

    const removeTokenMutation = useMutation({
        mutationFn: () => api.delete<{ message: string }>(`/areals/${arealId}/beskyd-card-config`),
        onSuccess: () => {
            toast.success(t("admin.settings.bvc.removed"))
            setLastTestedName(null)
            void qc.invalidateQueries({ queryKey: ["bvc-config", arealId] })
        },
        onError: () => toast.error(t("admin.settings.bvc.saveFailed")),
    })

    const testMutation = useMutation({
        mutationFn: () => api.post<{ ok: boolean; bvcDisplayName: string; bvcName: string }>(
            `/areals/${arealId}/beskyd-card-config/test`, {},
        ),
        onSuccess: (data) => {
            setLastTestedName(data.bvcDisplayName)
            toast.success(t("admin.settings.bvc.testOk", { name: data.bvcDisplayName }))
            void qc.invalidateQueries({ queryKey: ["bvc-config", arealId] })
        },
        onError: (e) => {
            const msg = e instanceof ApiRequestError ? e.message : t("admin.settings.bvc.testFailed")
            toast.error(msg)
        },
    })

    const envMutation = useMutation({
        mutationFn: (env: "test" | "prod") =>
            api.put<{ env: "test" | "prod"; reset: boolean }>(`/areals/${arealId}/beskyd-card-config/env`, { env }),
        onSuccess: (data) => {
            toast.success(data.env === "prod"
                ? t("admin.settings.bvc.envSwitchedProd")
                : t("admin.settings.bvc.envSwitchedTest"))
            setLastTestedName(null)
            void qc.invalidateQueries({ queryKey: ["bvc-config", arealId] })
        },
        onError: (e) => {
            const msg = e instanceof ApiRequestError ? e.message : t("admin.settings.bvc.envSwitchFailed")
            toast.error(msg)
        },
    })

    const enableMutation = useMutation({
        mutationFn: (enabled: boolean) =>
            api.put<{ enabled: boolean }>(`/areals/${arealId}/beskyd-card-config/enabled`, { enabled }),
        onSuccess: (data) => {
            toast.success(data.enabled
                ? t("admin.settings.bvc.enabledOn")
                : t("admin.settings.bvc.enabledOff"))
            void qc.invalidateQueries({ queryKey: ["bvc-config", arealId] })
        },
        onError: (e) => {
            const msg = e instanceof ApiRequestError ? e.message : t("admin.settings.bvc.enableFailed")
            toast.error(msg)
        },
    })

    if (isLoading || !status) {
        return (
            <div className={sectionClass}>
                <h2 className="text-lg font-black text-slate-900">{t("admin.settings.bvc.section")}</h2>
                <p className="text-sm font-bold text-slate-400">{t("common.loading")}</p>
            </div>
        )
    }

    const canTest = status.hasToken
    const canEnable = status.hasToken && !!status.testedAt
    const displayName = lastTestedName ?? status.bvcDisplayName

    return (
        <div className={sectionClass}>
            <h2 className="text-lg font-black text-slate-900">{t("admin.settings.bvc.section")}</h2>
            <p className="text-sm font-bold text-slate-500">{t("admin.settings.bvc.intro")}</p>

            <div className="space-y-2">
                <label className={labelClass}>{t("admin.settings.bvc.tokenLabel")}</label>
                <input
                    type="password"
                    autoComplete="off"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    placeholder={status.hasToken
                        ? t("admin.settings.bvc.tokenSet")
                        : t("admin.settings.bvc.tokenPlaceholder")}
                    className={inputClass}
                />
                <p className="text-xs font-bold text-slate-400">{t("admin.settings.bvc.tokenHelp")}</p>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        disabled={!tokenInput.trim() || saveTokenMutation.isPending}
                        onClick={() => saveTokenMutation.mutate(tokenInput.trim())}
                        className={cn(primaryBtnClass, "disabled:opacity-40")}
                    >
                        {t("admin.settings.bvc.saveToken")}
                    </button>
                    {status.hasToken && (
                        <button
                            type="button"
                            disabled={removeTokenMutation.isPending}
                            onClick={() => removeTokenMutation.mutate()}
                            className="px-5 py-2.5 rounded-[28px] bg-red-50 text-red-600 font-black text-sm hover:bg-red-100 transition-colors active:scale-95 disabled:opacity-40"
                        >
                            {t("admin.settings.bvc.removeToken")}
                        </button>
                    )}
                </div>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-2">
                <label className={labelClass}>{t("admin.settings.bvc.envLabel")}</label>
                <select
                    value={status.env}
                    disabled={envMutation.isPending}
                    onChange={(e) => envMutation.mutate(e.target.value === "prod" ? "prod" : "test")}
                    className={cn(inputClass, "disabled:opacity-40")}
                >
                    <option value="test">{t("admin.settings.bvc.envTest")}</option>
                    <option value="prod">{t("admin.settings.bvc.envProd")}</option>
                </select>
                <p className="text-xs font-bold text-slate-400">{t("admin.settings.bvc.envHelp")}</p>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        disabled={!canTest || testMutation.isPending}
                        onClick={() => testMutation.mutate()}
                        className="px-5 py-2.5 rounded-[28px] bg-slate-700 text-white font-black text-sm hover:bg-slate-800 transition-colors active:scale-95 disabled:opacity-40"
                    >
                        {testMutation.isPending ? t("admin.settings.bvc.testing") : t("admin.settings.bvc.testButton")}
                    </button>
                    <div className="text-xs font-bold text-slate-500">
                        {status.testedAt
                            ? t("admin.settings.bvc.lastTested", { when: new Date(status.testedAt).toLocaleString() })
                            : t("admin.settings.bvc.neverTested")}
                    </div>
                </div>
                {displayName && (
                    <div className="text-xs font-bold text-emerald-600">{displayName}</div>
                )}
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={status.enabled}
                        disabled={!canEnable || enableMutation.isPending}
                        onChange={(e) => enableMutation.mutate(e.target.checked)}
                        className="size-4 rounded border-slate-300 text-[#76d248] focus:ring-[#76d248] disabled:opacity-40"
                    />
                    <span className="font-black text-sm text-slate-900">
                        {t("admin.settings.bvc.enableLabel")}
                    </span>
                </label>
                <p className="text-xs font-bold text-slate-400 pl-7">
                    {!status.hasToken
                        ? t("admin.settings.bvc.enableNeedsToken")
                        : !status.testedAt
                            ? t("admin.settings.bvc.enableNeedsTest")
                            : t("admin.settings.bvc.enableHelp")}
                </p>
            </div>
        </div>
    )
}
